import {
    useContext, useEffect, useMemo, useState,
} from 'react';
import { DataGrid, GridColumnHeaderSortIcon, useGridApiRef } from '@mui/x-data-grid';
import {
    Box, IconButton, MenuItem, TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import _ from 'lodash';

import { UserActions } from 'BMapsCmds';
import { useDispatch, useSelector } from 'BMapsSrc/redux';
import { jobDefinitions, lookupJobDefinition } from 'BMapsSrc/jobs/JobDefinition';
import { updateDisplayedCompoundJobs, updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import DialogContentSection from 'BMapsSrc/ui/common/DialogContentSection';
import BMapsTooltip from 'BMapsSrc/ui/BMapsTooltip';
import { HoverableCompoundStructureCell } from 'BMapsSrc/ui/tables/TableCells';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsSrc/ui/common/DialogActionButtons';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import LoadingIconButton from 'BMapsSrc/ui/common/LoadingIconButton';
import Select from 'BMapsSrc/ui/common/Select';
import { ProteinContext } from 'BMapsSrc/ui/common/ProteinContextProvider';
import ProteinSelect from 'BMapsSrc/ui/common/ProteinSelect';
import { fetchJobs } from 'BMapsSrc/redux/jobs/thunks';
import { setJobsCompoundsTableState } from 'BMapsSrc/redux/projectState/access';
import { GridToolBar } from 'BMapsSrc/ui/tables/CustomToolbar';
import { CustomColumnsPanel } from 'BMapsSrc/ui/tables/CustomColumnsPanel';
import { getPropertyTooltip } from 'BMapsSrc/integrations/properties_rendering';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import { MolDataSource } from 'BMapsSrc/utils';
import { JobsGrid } from '../../ImportProteinPane/Tabs/JobsStructuresTab';
import { getPlacementOptions, PlacementTypes, getMoleculeOptions } from '../../tab_utils';
import { InputBox } from '../../import_components';

export function JobsCompoundsTab({ initData, closeImportPane, selected }) {
    const {
        jobs, status, localJobState, displayedCompoundJobs,
    } = useSelector((state) => state.jobs);
    const activeJobs = displayedCompoundJobs;
    const setActiveJobs = (jobList) => updateDisplayedCompoundJobs(ensureArray(jobList));
    const dispatch = useDispatch();
    const retrieveJobs = () => dispatch(fetchJobs());
    const highlightedJob = initData?.jobid;
    const [flashId, setFlashId] = useState(null);
    const [errors, setErrors] = useState([]);

    const loadJobsList = async () => {
        const { error } = await retrieveJobs();
        if (error) {
            setErrors([error]);
        } else {
            setErrors([]);
        }
    };

    useEffect(() => {
        if (status === 'unloaded') {
            loadJobsList();
        }
    }, [status]);

    // Animates the flash when a new job is created, so that it can be easily visible
    useEffect(() => {
        if (!highlightedJob) return () => null;
        setFlashId(highlightedJob);

        const timer = setTimeout(() => setFlashId(null), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [highlightedJob]);

    if (!selected) return null;
    const myJobs = jobs.filter((job) => lookupJobDefinition(job.jobtype)?.loadsCompound);

    return (
        <>
            <DialogContentSection
                title="Compound Generation Jobs"
                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                contentStyle={{ display: 'flex', flexDirection: 'column' }}
                titleIcon={(
                    <BMapsTooltip title="Refresh Jobs List">
                        <LoadingIconButton Icon={RefreshIcon} load={loadJobsList} />
                    </BMapsTooltip>
                )}
            >
                <JobsGrid
                    jobs={myJobs}
                    localJobState={localJobState}
                    cleanup={() => {}}
                    flashId={flashId}
                    preventMultiSelect
                    errors={errors}
                    setActiveJobs={setActiveJobs}
                />
            </DialogContentSection>
            {!!activeJobs && (
                <DialogContentSection title="Job Results">
                    <CompoundJobGrid
                        closeImportPane={closeImportPane}
                        activeJobs={activeJobs}
                        localJobState={localJobState}
                    />
                </DialogContentSection>
            )}
        </>
    );
}

function CompoundJobGrid({
    closeImportPane, activeJobs, localJobState,
}) {
    const apiRef = useGridApiRef();
    const tableState = useSelector((state) => state.projectState.tableState.jobsCompoundsTable);
    const columnWidths = tableState?.columnWidths || {};
    const setColumnWidths = (widths) => {
        setJobsCompoundsTableState({
            columnWidths: widths,
        });
    };

    const compoundJobs = activeJobs.map((job) => (
        {
            job,
            structures: localJobState[`${job.jobid}`]?.structures,
        }
    ));

    const { selectedCaseData, selectedProtein } = useContext(ProteinContext);
    const [moleculeName, setMoleculeName] = useState('');
    const [protein, setProtein] = useState({
        protein: selectedProtein,
        caseData: selectedCaseData,
    });
    const [checkedRowItems, setCheckedRowItems] = useState([]);
    const placementOptions = useMemo(() => {
        let format = 'smi';
        if (checkedRowItems.length > 0) {
            if (checkedRowItems.some(({ mol }) => mol)) format = 'mol';
        } else {
            if (compoundJobs.some((cj) => cj.structures.some((s) => s.mol))) format = 'mol';
        }
        const {
            availableOptions, disabledOptions,
        } = getPlacementOptions(format, protein.caseData);
        return [...availableOptions, ...disabledOptions];
    }, [protein, checkedRowItems]);
    const [placement, setPlacement] = useState(() => {
        const allMol = compoundJobs.every((cj) => cj.structures.every((s) => s.mol));
        return allMol ? PlacementTypes.KeepExisting : PlacementTypes.PlaceOutside;
    });
    function findPlacement(value, placementOptionList) {
        return placementOptionList.find((option) => option.value === value);
    }

    /**
     * @param {Array<Object>} smiles
     */
    async function importMolecules(structures) {
        let placementOption;
        if (checkedRowItems.length > 0) {
            placementOption = findPlacement(placement, placementOptions);
        } else if (structures[0].mol) {
            placementOption = { placementType: PlacementTypes.KeepExisting };
        } else {
            placementOption = { placementType: PlacementTypes.PlaceOutside };
        }
        const { placementType, ligandspec: refSpec } = placementOption;
        const moleculeLoadOptions = getMoleculeOptions(placementType, true, refSpec || '', false);
        for (const structure of structures) {
            let moldata = structure.SMILES;
            let format = 'smi';
            if (structure.mol) {
                moldata = structure.mol;
                format = 'mol';
            }
            const jobDefinition = jobDefinitions[structure.jobtype];

            const molSource = MolDataSource.FromCompoundGenerationJob(
                moleculeName, format, moldata, null
            );
            const { compounds: [compound] } = await UserActions.LoadMolData(
                molSource, moleculeLoadOptions, protein?.caseData
            );
            const builtinKeys = ['jobtype', 'jobid', 'id', 'SMILES', 'mol'];
            const properties = Object.keys(structure).filter((key) => !builtinKeys.includes(key));
            const propertyScope = jobDefinition.propertyScope || jobDefinition.jobTypeId;
            for (const property of properties) {
                compound.setProperty(propertyScope, property, structure[property]);
            }
        }
    }

    function removeMolecules(rows) {
        const removedJobs = {};
        for (const row of rows) {
            const jobId = row.jobid;
            const jobDefinition = jobDefinitions[row.jobtype];
            const idField = jobDefinition.idField || 'id';
            const removedCompound = compoundJobs.flatMap((job) => job.structures)
                .find((compound) => compound[idField] === row.id);
            if (!removedCompound) {
                continue;
            }
            if (!removedJobs[jobId]) {
                removedJobs[jobId] = [];
            }
            removedJobs[jobId].push(removedCompound);
        }
        for (const [key, value] of Object.entries(removedJobs)) {
            const currentJobState = localJobState[key];
            const updatedJobState = currentJobState.structures.filter(
                (comp) => !value.includes(comp)
            );

            updateLocalJobState(key, { structures: updatedJobState });
        }
    }

    const { columns, rows } = buildCompoundGrid(
        compoundJobs, importMolecules, removeMolecules, columnWidths
    );

    function validatePlacement(place) {
        return !PlacementTypes.isValidPlacement(place)
            ? 'Please choose an option for compound placement!'
            : undefined;
    }

    const placementUIOptions = placementOptions
        .map((option) => {
            const {
                label, disabled, value, disabledMsg='', title='',
            } = option;
            const titleText = disabled ? disabledMsg : title;

            return (
                <MenuItem
                    key={label}
                    value={value}
                    disabled={disabled}
                    title={titleText}
                >
                    {label}
                </MenuItem>
            );
        });

    function handleColumnWidthChange(params) {
        const newWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(newWidths);
    }

    return (
        <>
            <DataGrid
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                disableColumnMenu={false}
                disableRowSelectionOnClick
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 10, page: 0 },
                    },
                }}
                pageSizeOptions={[10, 25, 50]}
                checkboxSelection
                onRowSelectionModelChange={(rowIds) => {
                    const structures = rows.filter((row) => rowIds.includes(row.id));
                    setCheckedRowItems(structures);
                }}
                onColumnVisibilityModelChange={(columnVisibilityModel) => {
                    setJobsCompoundsTableState({ columnVisibilityModel });
                }}
                columnVisibilityModel={tableState?.columnVisibilityModel
                    || getDefaultColumnVisibilityModel(compoundJobs)}
                slots={{
                    toolbar: (toolBarProps) => (
                        <GridToolBar
                            {...toolBarProps}
                            exportFileName="name"
                            toolBarId="JobsCompoundsTable"
                        />
                    ),
                    columnsPanel: CustomColumnsPanel,
                    columnHeaderSortIcon: (props) => {
                        const found = columns.find((c) => c.field === props.field);
                        const title = found ? `Sort by ${found.headerName}` : 'Sort';
                        return (
                            <BMapsTooltip title={title} noMaxWidth>
                                <GridColumnHeaderSortIcon {...props} />
                            </BMapsTooltip>
                        );
                    },
                }}
                showToolbar
                onColumnWidthChange={handleColumnWidthChange}
            />
            <InputBox sx={{
                display: 'flex', flexDirection: 'column', gap: '1rem', margin: '1rem 0',
            }}
            >
                <ProteinSelect onChange={(e, value) => setProtein(value)} />
                <TextField
                    fullWidth
                    name="molName"
                    label="Molecule Name (optional)"
                    variant="outlined"
                    optimize={false}
                    value={moleculeName}
                    onChange={(e) => {
                        setMoleculeName(e.target.value);
                    }}
                />
                <Select
                    name="placement"
                    label="Compound Placement"
                    validate={validatePlacement}
                    SelectProps={{
                        native: false,
                    }}
                    value={placement}
                    onChange={(e) => {
                        setPlacement(e.target.value);
                    }}
                >
                    {placementUIOptions}
                </Select>
            </InputBox>
            <BMapsTooltip title={checkedRowItems.length <= 0 ? 'Select a Compound Result to Load' : 'Load Selected Compound Results'}>
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton
                        type="button"
                        variant="contained"
                        sx={{ width: '100%', marginBottom: '1rem' }}
                        onClick={async () => {
                            await importMolecules(checkedRowItems);
                            closeImportPane();
                        }}
                        disabled={checkedRowItems.length <= 0}
                    >
                        Load Compounds
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip title={checkedRowItems.length <= 0 ? 'Select a Compound to Remove' : 'Remove Selected Compound Results'}>
                {wrapForDisabledTooltip(
                    <DialogCancelButton
                        type="button"
                        sx={{ width: '100%' }}
                        onClick={() => {
                            removeMolecules(checkedRowItems);
                        }}
                        disabled={checkedRowItems.length <= 0}
                    >
                        Remove Compounds
                    </DialogCancelButton>
                )}
            </BMapsTooltip>
        </>
    );
}

function buildCompoundGrid(compoundJobs, loadAction, removeAction, columnWidths) {
    const columns = [];
    let rows = [];
    function addOneColumn(key, propInfo={}) {
        if (propInfo.excludeFromTable) return;
        if (columns.find((col) => col.field === key)) return;
        const tooltip = dataInfoTooltip(propInfo);
        const columnInfo = {
            field: key,
            headerName: propInfo.label || key,
            title: propInfo.label || key,
            type: 'number',
            minWidth: 80,
            width: columnWidths[key],
            description: tooltip,
            renderCell: ({ value }) => (
                <BMapsTooltip title={value} placement="bottom-start">
                    <span>{value}</span>
                </BMapsTooltip>
            ),
            renderHeader: () => (
                <BMapsTooltip title={tooltip} noMaxWidth>
                    <span>{propInfo.label || key}</span>
                </BMapsTooltip>
            ),
        };
        if (propInfo.structureValue) {
            columnInfo.renderCell = ({ value, row }) => (
                <HoverableCompoundStructureCell smiles={value} row={row} />
            );
            columnInfo.sortable = false;
            columnInfo.filterable = false;
            columnInfo.type = 'string';
        }
        columns.push(columnInfo);
    }

    for (const { job: activeJob, structures } of compoundJobs) {
        const jobDefinition = jobDefinitions[activeJob?.jobtype];
        const knownColumns = jobDefinition?.dataInfo || {};
        const idField = jobDefinition?.idField || 'id';
        // Add explicitly defined columns
        for (const [key, propInfo] of Object.entries(knownColumns)) {
            addOneColumn(key, propInfo);
        }
        for (const structure of structures) {
            const tableRow = {
                ...structure,
                id: structure[idField],
                jobtype: activeJob.jobtype,
                jobid: activeJob.jobid,
            };
            rows.push(tableRow);
            // Add any extra columns that are not explicitly defined
            for (const key of Object.keys(structure)) {
                addOneColumn(key, knownColumns[key]);
            }
        }
    }
    columns.push({
        field: 'Actions',
        headerName: ' ',
        title: 'Compound Actions',
        flex: 1,
        minWidth: 75,
        sortable: false,
        filterable: false,
        hideable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }) => {
            const noun = 'Compound';
            return (
                <Box>
                    <BMapsTooltip title={`Load ${noun}`}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                loadAction([row]);
                            }}
                        >
                            <AddCircleOutlineIcon fontSize="inherit" />
                        </IconButton>
                    </BMapsTooltip>
                    <BMapsTooltip title={`Remove ${noun}`}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                removeAction([row]);
                            }}
                        >
                            <ClearIcon fontSize="inherit" />
                        </IconButton>
                    </BMapsTooltip>
                </Box>
            );
        },
    });
    rows = _.uniqBy(rows, (r) => r.id)
        .filter((r) => r.id);
    if (!rows || rows.length === 0) {
        return { rows: [], columns: [] };
    }
    return { rows, columns };
}

function getDefaultColumnVisibilityModel(compoundJobs) {
    const columnVisibilityModelEntries = [];
    for (const { job: activeJob, structures } of compoundJobs) {
        const jobDefinition = jobDefinitions[activeJob?.jobtype];
        if (jobDefinition) {
            // Collect all property metadata keys, either explicitly defined or from structures
            let keys = new Set(Object.keys(jobDefinition.dataInfo || {}));
            for (const structure of structures) {
                for (const key of Object.keys(structure)) {
                    keys.add(key);
                }
            }
            keys = [...keys];
            // All metadata columns default to not visible unless otherwise stated
            columnVisibilityModelEntries.push(...keys
                .filter((key) => !jobDefinition.dataInfo[key]?.defaultVisible)
                .map((key) => [key, false])); // Preparing entry list for fromEntries below
        }
    }
    return (
        Object.fromEntries(columnVisibilityModelEntries)
    );
}
function dataInfoTooltip(dataInfo, heading) {
    let tooltip = dataInfo.label;
    const description = dataInfo.description;
    if (description) tooltip += `: ${description}`;
    if (Object.keys(dataInfo).length > 1) {
        const metaDataTooltip = getPropertyTooltip(heading, dataInfo);
        if (metaDataTooltip) tooltip = metaDataTooltip;
    }
    return tooltip;
}
